import { DownloadOutlined, PrinterOutlined } from "@ant-design/icons";
import { Alert, Button, Space, Table, Tag, Typography } from "antd";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";

import { useCandidates } from "../../api/candidates";
import { useAnswerSheet } from "../../api/exports";
import { formatTime } from "../../api/format";
import { useNames } from "../../api/names";
import { downloadBlob } from "../../api/scores";
import type { CandidateSummary } from "../../api/types";
import { CandidateFilterBar, type CandidateFilters } from "../candidates/CandidateFilterBar";

const PAGE_SIZE = 20;

/** Same filters as the candidate list; pick a candidate to render the printable sheet in an iframe. */
export function AnswerSheetsPage() {
  const [params, setParams] = useSearchParams();
  const requested = params.get("username") ?? undefined;
  const [filters, setFilters] = useState<CandidateFilters>({ keyword: requested });
  const [page, setPage] = useState(1);
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const sheet = useAnswerSheet();
  const list = useCandidates({ ...filters, order, page, pageSize: PAGE_SIZE });
  const { groupName, regionName, siteName, roomName } = useNames();
  const frame = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (requested) sheet.mutate(requested);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requested]);

  const open = (username: string) => setParams({ username });

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <CandidateFilterBar
        value={filters}
        onChange={(next) => { setFilters(next); setPage(1); }}
        refreshing={list.isFetching}
        onRefresh={() => void list.refetch()}
        extra={<Typography.Text type="secondary">点击准考证号查看答卷，浏览器打印即可得到 PDF。</Typography.Text>}
      />
      {list.error && <Alert type="error" showIcon message={list.error.message} />}
      <Table<CandidateSummary>
        rowKey="username"
        size="small"
        loading={list.isLoading}
        dataSource={list.data?.items ?? []}
        rowClassName={(row) => row.username === sheet.data?.username ? "ant-table-row-selected" : ""}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: list.data?.total ?? 0,
          showSizeChanger: false,
          showTotal: (total) => `共 ${total} 人`,
          onChange: setPage
        }}
        onChange={(_, __, sorter) => {
          const next = (Array.isArray(sorter) ? sorter[0] : sorter)?.order === "descend" ? "desc" : "asc";
          if (next !== order) { setOrder(next); setPage(1); }
        }}
        columns={[
          { title: "准考证号", dataIndex: "username", sorter: true, sortOrder: order === "asc" ? "ascend" : "descend", sortDirections: ["ascend", "descend", "ascend"],
            render: (value: string) => <a onClick={() => open(value)}>{value}</a> },
          { title: "姓名", dataIndex: "name" },
          { title: "组别", dataIndex: "group", render: groupName },
          { title: "考点区域", dataIndex: "regionId", render: regionName },
          { title: "考点", dataIndex: "siteId", render: siteName },
          { title: "考场", dataIndex: "roomId", render: roomName },
          { title: "座位", dataIndex: "seat", render: (value: string | null) => value ?? "—" },
          { title: "状态", render: (_, row) => (
            <Space size={4}>
              {row.enabled ? <Tag color="green">启用</Tag> : <Tag color="red">已关闭</Tag>}
              {row.lastSubmitAt ? <Tag color="blue">已提交</Tag> : <Tag>未提交</Tag>}
            </Space>
          ) },
          { title: "最近提交", dataIndex: "lastSubmitAt", render: formatTime },
          { title: "操作", render: (_, row) => <a onClick={() => open(row.username)}>查看答卷</a> }
        ]}
      />
      {sheet.error && <Alert type="error" showIcon message={sheet.error.message} />}
      {sheet.data && (
        <>
          <Space>
            <Typography.Text strong>{sheet.data.name}（{sheet.data.username}）</Typography.Text>
            <Button icon={<PrinterOutlined />} onClick={() => frame.current?.contentWindow?.print()}>打印</Button>
            <Button icon={<DownloadOutlined />} onClick={() => downloadBlob(sheet.data.filename, new Blob([sheet.data.html], { type: "text/html;charset=utf-8" }))}>下载 HTML</Button>
          </Space>
          <iframe ref={frame} title="答卷" srcDoc={sheet.data.html} className="sheet-frame" />
        </>
      )}
    </Space>
  );
}
